import { useEffect, useState, useCallback } from 'react'
import { FileText, Plus, X, Trash2, RefreshCw, Search, Github } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Doc {
  id: number
  service: string
  repo: string
  source: string
  body_md: string
  fetched_at: string
}

export default function ServiceReadmePage() {
  const [items, setItems] = useState<Doc[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [showFetch, setShowFetch] = useState(false)
  const [filter, setFilter] = useState('')
  const [selected, setSelected] = useState<Doc | null>(null)
  const [form, setForm] = useState({ service: '', repo: '', source: 'manual', body_md: '' })
  const [fetchForm, setFetchForm] = useState({ service: '', repo: '' })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/res/readmes')
      setItems(r.data?.readmes || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const create = async () => {
    if (!form.service) return
    await api.post('/api/res/readmes', form)
    setShowForm(false)
    setForm({ service: '', repo: '', source: 'manual', body_md: '' })
    fetchAll()
  }

  const fetchGithub = async () => {
    if (!fetchForm.service || !fetchForm.repo) return
    try {
      await api.post('/api/res/readmes/fetch-from-github', fetchForm)
      setShowFetch(false)
      setFetchForm({ service: '', repo: '' })
      fetchAll()
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'fetch failed')
    }
  }

  const remove = async (id: number) => {
    if (!confirm('Delete?')) return
    await api.delete(`/api/res/readmes/${id}`)
    if (selected?.id === id) setSelected(null)
    fetchAll()
  }

  const filtered = filter
    ? items.filter(i => i.service.toLowerCase().includes(filter.toLowerCase())
                     || i.repo.toLowerCase().includes(filter.toLowerCase()))
    : items

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <FileText size={24} className="text-blue-light" /> Service READMEs
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            One searchable place for every service's docs. Pull from GitHub or paste markdown.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs"><RefreshCw size={11} className={loading ? 'animate-spin' : ''} /></button>
          <button type="button" onClick={() => setShowFetch(true)} className="btn-secondary text-xs"><Github size={12} /> From GitHub</button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs"><Plus size={12} /> Paste README</button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
        <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="filter…"
          className="input pl-7 py-1.5 text-[11px] w-full" />
      </div>

      {showFetch && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Fetch README from GitHub</h3>
            <button type="button" onClick={() => setShowFetch(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={fetchForm.service} onChange={e => setFetchForm({...fetchForm, service: e.target.value})} placeholder='Service name e.g. "auth-api"' className="input py-1.5" />
            <input value={fetchForm.repo} onChange={e => setFetchForm({...fetchForm, repo: e.target.value})} placeholder='Repo e.g. "owner/auth-api"' className="input py-1.5 font-mono" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowFetch(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={fetchGithub} className="btn-primary text-xs">Fetch</button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Paste README</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.service} onChange={e => setForm({...form, service: e.target.value})} placeholder="Service" className="input py-1.5" />
            <input value={form.repo} onChange={e => setForm({...form, repo: e.target.value})} placeholder="Repo (optional)" className="input py-1.5 font-mono" />
            <textarea value={form.body_md} onChange={e => setForm({...form, body_md: e.target.value})} rows={10} placeholder="README markdown" className="input py-1.5 md:col-span-2 font-mono text-[11px]" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-1 card p-2 max-h-[600px] overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-center py-6 text-text-muted text-[11px] italic">No docs.</p>
          ) : (
            <div className="space-y-1">
              {filtered.map(d => (
                <button key={d.id} type="button" onClick={() => setSelected(d)}
                  className={clsx('w-full text-left p-2 rounded text-[11px] transition-colors',
                    selected?.id === d.id ? 'bg-blue/15 text-text-primary' : 'hover:bg-surface-hover text-text-secondary')}>
                  <div className="font-medium">{d.service}</div>
                  <div className="text-[9px] text-text-muted font-mono truncate">{d.repo || '—'} · {d.source}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="md:col-span-2 card p-3">
          {selected ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">{selected.service}</h3>
                  <div className="text-[10px] text-text-muted font-mono">
                    {selected.repo || '—'} · {selected.source} · fetched {selected.fetched_at?.slice(0, 16).replace('T', ' ')}
                  </div>
                </div>
                <button type="button" onClick={() => remove(selected.id)} className="text-text-muted hover:text-rose"><Trash2 size={11} /></button>
              </div>
              <pre className="p-3 bg-surface-raised rounded text-[11px] font-mono text-text-secondary whitespace-pre-wrap max-h-[520px] overflow-y-auto">
                {selected.body_md || '(empty)'}
              </pre>
            </>
          ) : (
            <p className="text-center py-12 text-text-muted text-[11px] italic">Select a service to view.</p>
          )}
        </div>
      </div>
    </div>
  )
}
